import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Card,
  CardContent,
  IconButton,
  Chip,
  Autocomplete,
  LinearProgress,
  Alert,
  Breadcrumbs,
  Link,
} from '@mui/material'
import { 
  ArrowBack as ArrowBackIcon,
  Upload as UploadIcon,
  Add as AddIcon, 
  Delete as DeleteIcon,
  Download as DownloadIcon,
  AttachMoney as PricingIcon,
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import {
  useGetCustomersQuery,
  useGetPricingTiersQuery,
  useBulkUpdatePricingMutation,
} from '../store/services/customerApi'
import { useGetProductsQuery } from '../store/services/productApi'
import { Customer } from '../types/customer'
import PageHeader from '../components/ui/PageHeader'

interface PriceAdjustment {
  product_id: string
  product_name?: string
  adjustment_type: 'percentage' | 'fixed_amount' | 'fixed_price'
  adjustment_value: number
}

const BulkPricingPage: React.FC = () => {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()

  // Form state
  const [updateType, setUpdateType] = useState<'tier' | 'customers'>('tier')
  const [selectedTier, setSelectedTier] = useState<string>('')
  const [selectedCustomers, setSelectedCustomers] = useState<Customer[]>([])
  const [priceAdjustments, setPriceAdjustments] = useState<PriceAdjustment[]>([])
  const [loading, setLoading] = useState(false)
  const [progress, setProgress] = useState(0)

  // API hooks
  const { data: customersData } = useGetCustomersQuery({
    page: 1,
    per_page: 100,
    is_active: true,
  })

  const { data: tiersData } = useGetPricingTiersQuery({
    page: 1,
    per_page: 100,
    is_active: true,
  })

  const { data: productsData } = useGetProductsQuery({
    page: 1,
    per_page: 100,
    status: 'ACTIVE',
  })

  const [bulkUpdatePricing] = useBulkUpdatePricingMutation()

  // Reset form when component mounts
  useEffect(() => {
    setUpdateType('tier')
    setSelectedTier('')
    setSelectedCustomers([])
    setPriceAdjustments([])
    setProgress(0)
  }, [])

  const addPriceAdjustment = () => {
    setPriceAdjustments(prev => [...prev, {
      product_id: '',
      adjustment_type: 'percentage',
      adjustment_value: 0,
    }])
  }

  const updatePriceAdjustment = (index: number, field: keyof PriceAdjustment, value: any) => {
    setPriceAdjustments(prev => prev.map((adj, i) => 
      i === index ? { ...adj, [field]: value } : adj
    ))
  }

  const removePriceAdjustment = (index: number) => {
    setPriceAdjustments(prev => prev.filter((_, i) => i !== index))
  }

  const handleProductSelect = (index: number, productId: string) => {
    const product = productsData?.items.find(p => p.id === productId)
    updatePriceAdjustment(index, 'product_id', productId)
    updatePriceAdjustment(index, 'product_name', product?.name || '')
  }

  const validateForm = (): boolean => {
    if (updateType === 'tier' && !selectedTier) {
      enqueueSnackbar('Please select a pricing tier', { variant: 'error' })
      return false
    }

    if (updateType === 'customers' && selectedCustomers.length === 0) {
      enqueueSnackbar('Please select at least one customer', { variant: 'error' })
      return false
    }

    if (priceAdjustments.length === 0) {
      enqueueSnackbar('Please add at least one price adjustment', { variant: 'error' })
      return false
    }

    for (const adj of priceAdjustments) {
      if (!adj.product_id) {
        enqueueSnackbar('Please select a product for all adjustments', { variant: 'error' })
        return false
      }
      if (adj.adjustment_value === 0) {
        enqueueSnackbar('Adjustment value cannot be zero', { variant: 'error' })
        return false
      }
    }

    return true
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      return
    }

    setLoading(true)
    setProgress(0)

    try {
      // Convert adjustments to the expected format
      const adjustments: Record<string, number> = {}
      priceAdjustments.forEach(adj => {
        const key = `${adj.product_id}_${adj.adjustment_type}`
        adjustments[key] = adj.adjustment_value
      })

      const requestData = {
        price_adjustments: adjustments,
        ...(updateType === 'tier' ? { tier_id: selectedTier } : {}),
        ...(updateType === 'customers' ? { customer_ids: selectedCustomers.map(c => c.id) } : {}),
      }

      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setProgress(prev => Math.min(prev + 10, 90))
      }, 200)

      const result = await bulkUpdatePricing(requestData).unwrap()
      
      clearInterval(progressInterval)
      setProgress(100)

      enqueueSnackbar(
        `Bulk pricing update completed. ${result.updated_count} records updated.`,
        { variant: 'success' }
      )
      
      setTimeout(() => {
        navigate('/customers')
      }, 1000)
    } catch (error: any) {
      enqueueSnackbar(
        error?.data?.detail || 'Failed to update pricing',
        { variant: 'error' }
      )
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    navigate('/customers')
  }

  const handleCSVTemplate = () => {
    // Generate CSV template
    const headers = ['product_id', 'product_name', 'adjustment_type', 'adjustment_value']
    const sampleData = [
      ['product-123', 'Sample Product', 'percentage', '10'],
      ['product-456', 'Another Product', 'fixed_amount', '50'],
      ['product-789', 'Third Product', 'fixed_price', '999'],
    ]
    
    const csvContent = [headers, ...sampleData]
      .map(row => row.join(','))
      .join('\n')
    
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'bulk_pricing_template.csv'
    a.click()
    window.URL.revokeObjectURL(url)
  }

  const availableCustomers = customersData?.items || []
  const availableTiers = tiersData?.items || []
  const availableProducts = productsData?.items || []

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/customers')}
          sx={{ textDecoration: 'none' }}
        >
          Customers
        </Link>
        <Typography color="text.primary">
          Bulk Pricing Update
        </Typography>
      </Breadcrumbs>

      <PageHeader
        title="Bulk Pricing Update"
        subtitle="Update pricing for multiple customers or entire pricing tiers"
        actions={[
          {
            label: 'Cancel',
            onClick: handleCancel,
            variant: 'outlined',
            icon: <ArrowBackIcon />,
            disabled: loading,
          },
          {
            label: loading ? 'Updating...' : 'Apply Bulk Update',
            onClick: handleSubmit,
            variant: 'contained',
            color: 'secondary',
            icon: <UploadIcon />,
            disabled: loading || priceAdjustments.length === 0,
          },
        ]}
      />

      <Paper sx={{ p: 4 }}>
        {loading && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="body2" gutterBottom>
              Updating pricing... {progress}%
            </Typography>
            <LinearProgress variant="determinate" value={progress} sx={{ borderRadius: 1 }} />
          </Box>
        )}

        <Grid container spacing={4}>
          {/* Update Target Selection */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <PricingIcon color="primary" />
              <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                Update Target
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
              <InputLabel>Update Type</InputLabel>
              <Select
                value={updateType}
                onChange={(e) => setUpdateType(e.target.value as 'tier' | 'customers')}
                label="Update Type"
                disabled={loading}
              >
                <MenuItem value="tier">Apply to Pricing Tier</MenuItem>
                <MenuItem value="customers">Apply to Specific Customers</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {updateType === 'tier' && (
            <Grid item xs={12} md={6}>
              <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                <InputLabel>Pricing Tier</InputLabel>
                <Select
                  value={selectedTier}
                  onChange={(e) => setSelectedTier(e.target.value)}
                  label="Pricing Tier"
                  disabled={loading}
                >
                  {availableTiers.map((tier) => (
                    <MenuItem key={tier.id} value={tier.id}>
                      {tier.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {updateType === 'customers' && (
            <Grid item xs={12} md={6}>
              <Autocomplete
                multiple
                options={availableCustomers}
                getOptionLabel={(option) => `${option.name} (${option.email})`}
                value={selectedCustomers}
                onChange={(_, newValue) => setSelectedCustomers(newValue)}
                disabled={loading}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option.name}
                      {...getTagProps({ index })}
                      key={option.id}
                      sx={{ borderRadius: 4 }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Customers"
                    placeholder="Choose customers"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                )}
              />
            </Grid>
          )}

          {/* Price Adjustments */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mt: 2, mb: 2 }}>
              <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                Price Adjustments
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  startIcon={<AddIcon />}
                  onClick={addPriceAdjustment}
                  variant="outlined"
                  size="small"
                  disabled={loading}
                  sx={{ borderRadius: 2 }}
                >
                  Add Adjustment
                </Button>
                <Button
                  startIcon={<DownloadIcon />}
                  onClick={handleCSVTemplate}
                  variant="outlined"
                  size="small"
                  disabled={loading}
                  sx={{ borderRadius: 2 }}
                >
                  Download Template
                </Button>
              </Box>
            </Box>
            <Divider sx={{ mb: 3 }} />

            {priceAdjustments.map((adjustment, index) => (
              <Card key={index} sx={{ mb: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Adjustment {index + 1}
                    </Typography>
                    <IconButton
                      onClick={() => removePriceAdjustment(index)}
                      color="error"
                      size="small"
                      disabled={loading}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>

                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                      <Autocomplete
                        options={availableProducts}
                        getOptionLabel={(option) => `${option.name} (${option.sku})`}
                        value={availableProducts.find(p => p.id === adjustment.product_id) || null}
                        onChange={(_, newValue) => handleProductSelect(index, newValue?.id || '')}
                        disabled={loading}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Product"
                            size="small"
                            required
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Adjustment Type</InputLabel>
                        <Select
                          value={adjustment.adjustment_type}
                          onChange={(e) => updatePriceAdjustment(index, 'adjustment_type', e.target.value)}
                          label="Adjustment Type"
                          disabled={loading}
                          sx={{ borderRadius: 2 }}
                        >
                          <MenuItem value="percentage">Percentage Change</MenuItem>
                          <MenuItem value="fixed_amount">Fixed Amount Change</MenuItem>
                          <MenuItem value="fixed_price">Set Fixed Price</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label={
                          adjustment.adjustment_type === 'percentage' ? 'Percentage (%)' :
                          adjustment.adjustment_type === 'fixed_amount' ? 'Amount (₹)' :
                          'Price (₹)'
                        }
                        type="number"
                        value={adjustment.adjustment_value}
                        onChange={(e) => updatePriceAdjustment(index, 'adjustment_value', Number(e.target.value))}
                        size="small"
                        disabled={loading}
                        inputProps={{
                          step: adjustment.adjustment_type === 'percentage' ? 0.1 : 0.01
                        }}
                        required
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>

                    <Grid item xs={12} md={2}>
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Typography variant="body2" color="text.secondary" textAlign="center">
                          {adjustment.adjustment_type === 'percentage' && 
                            `${adjustment.adjustment_value > 0 ? '+' : ''}${adjustment.adjustment_value}%`}
                          {adjustment.adjustment_type === 'fixed_amount' && 
                            `${adjustment.adjustment_value > 0 ? '+' : ''}₹${Math.abs(adjustment.adjustment_value)}`}
                          {adjustment.adjustment_type === 'fixed_price' && 
                            `₹${adjustment.adjustment_value}`}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {adjustment.adjustment_type === 'percentage' && 
                        `${adjustment.adjustment_value > 0 ? 'Increase' : 'Decrease'} price by ${Math.abs(adjustment.adjustment_value)}%`}
                      {adjustment.adjustment_type === 'fixed_amount' && 
                        `${adjustment.adjustment_value > 0 ? 'Add' : 'Subtract'} ₹${Math.abs(adjustment.adjustment_value)} to/from price`}
                      {adjustment.adjustment_type === 'fixed_price' && 
                        `Set price to ₹${adjustment.adjustment_value}`}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            ))}

            {priceAdjustments.length === 0 && (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                No price adjustments defined. Add adjustments to update product pricing.
              </Alert>
            )}
          </Grid>

          {/* Help Information */}
          <Grid item xs={12}>
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              <Typography variant="body2">
                <strong>Bulk Pricing Update:</strong>
                <br />
                • <strong>Percentage Change:</strong> Increase or decrease current price by percentage (e.g., +10% or -5%)
                <br />
                • <strong>Fixed Amount Change:</strong> Add or subtract a fixed amount from current price (e.g., +₹50 or -₹25)
                <br />
                • <strong>Set Fixed Price:</strong> Override current price with a specific amount (e.g., ₹999)
                <br />
                • Changes will be applied to all selected customers or all customers in the selected pricing tier
                <br />
                • Use the CSV template for bulk operations with many products
              </Typography>
            </Alert>
          </Grid>

          {/* Form Actions */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
              <Button
                variant="outlined"
                size="large"
                onClick={handleCancel}
                disabled={loading}
                sx={{ minWidth: 120, borderRadius: 2 }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                startIcon={loading ? undefined : <UploadIcon />}
                onClick={handleSubmit}
                disabled={loading || priceAdjustments.length === 0}
                sx={{ minWidth: 160, borderRadius: 2 }}
              >
                {loading ? 'Updating...' : 'Apply Bulk Update'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  )
}

export default BulkPricingPage